import { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import axios from "axios";
import getClassName from "../../utilities/getClassName";
import Fieldset from "../ui/Fieldset";
import debounce from "debounce-promise";
import envVariables from "../../data/envVariables.json";

// Icons
import fromIcon from "../../assets/icons/departure-time.svg";
import toIcon from "../../assets/icons/arrival-time.svg";
import fromIconRed from "../../assets/icons/departure-time-red.svg";
import toIconRed from "../../assets/icons/arrival-time-red.svg";

const PortalTailNumberInput = ({
    direction,
    value,
    defaultValue,
    label,
    validateForm,
    required,
    errorOnBlur,
    onFocus,
    copiedProperty,
    skeleton,
}) => {
    // State
    const [error, setError] = useState("");
    const [selectedValue, setSelectedValue] = useState(null);
    const [hideMenu, setHideMenu] = useState({ menuIsOpen: false });

    // Mount-only: seed from `copiedProperty` if the parent passed one.
    // Adding validationHandler/value to deps would loop because parents
    // pass inline arrows; copiedProperty is a one-shot init value.
    useEffect(() => {
        if (copiedProperty) {
            setSelectedValue(copiedProperty ? copiedProperty : selectedValue);
            validationHandler(copiedProperty ? copiedProperty : selectedValue);
            value(copiedProperty ? copiedProperty : selectedValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle selection change
    const selectChangeHandler = (selected) => {
        setSelectedValue(selected);
        validationHandler(selected);
        value(selected);
    };

    // Validate input
    const validationHandler = (validateSelection) => {
        switch (true) {
            case required && !validateSelection:
                setError(`${label} is required`);
                return;
            case required && validateSelection.length === 0:
                setError(`${label} is required`);
                return;
            default:
                setError("");
                return;
        }
    };

    // Load options using API call
    const debounceLoadOptions = debounce(async (inputValue) => {
        if (inputValue.length < 3) {
            setHideMenu({ menuIsOpen: false });
        }

        if (inputValue.length >= 4) {
            setHideMenu({});

            return axios
                .get(`${envVariables.apiEndpoint}/api/portal/get-aircraft-by-tail?tailNumber=${inputValue}`)
                .then((response) => {
                    return response.data;
                });
        }
    }, 300);

    // Set default value
    useEffect(() => {
        if (defaultValue) {
            setSelectedValue(defaultValue);
        } else {
            setSelectedValue(null);
        }
    }, [defaultValue]);

    // Force validation
    useEffect(() => {
        if (validateForm) {
            validationHandler(selectedValue);
        }
    });

    const focus = () => {
        if (onFocus) {
            onFocus();
        }
    };

    const getValue = () => {
        let value;

        if (copiedProperty) {
            value = copiedProperty;
        }

        if (selectedValue) {
            value = selectedValue;
        }

        return value;
    };

    return (
        <Fieldset skeleton={skeleton}>
            <div
                style={{
                    backgroundImage: `url(${error ? (direction === "to" ? toIconRed : fromIconRed) : direction === "to" ? toIcon : fromIcon})`,
                }}
                className={getClassName([
                    "select-input",
                    "select-input--from-to",
                    "select-input--flight-enquiry",
                    "select-input--full",
                    error && "select-input--flight-enquiry--error",
                ])}
            >
                <AsyncSelect
                    noOptionsMessage={() => "No aircraft Found"}
                    classNamePrefix="select-input"
                    cacheOptions
                    defaultOptions
                    value={getValue()}
                    getOptionLabel={(e) => "(" + e.id + ") " + e.aircraftName}
                    getOptionValue={(e) => {
                        return { tailNum: e.id, name: e.aircraftName, category: e.category, operator: e.operatorName };
                    }}
                    loadOptions={debounceLoadOptions}
                    onFocus={focus}
                    onChange={selectChangeHandler}
                    onBlur={errorOnBlur ? () => validationHandler(selectedValue) : () => {}}
                    placeholder={"Tail Num"}
                    {...hideMenu}
                />
            </div>
        </Fieldset>
    );
};

export default PortalTailNumberInput;
